import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, fmtInt } from './_finHelpers';

// Average annual U.S. college costs (representative)
const COLLEGE_TYPES = [
  { key: '4y-in-state', label: '4-year in-state public', cost: 30990 },
  { key: '4y-out-state', label: '4-year out-of-state public', cost: 48510 },
  { key: '4y-private', label: '4-year private nonprofit', cost: 58600 },
  { key: '2y-public', label: '2-year public (community)', cost: 19060 },
];

// College Cost Calculator — mirrors the reference image (input + dual scenario result)
export default function CollegeCostCalc() {
  const [collegeType, setCollegeType] = useState(COLLEGE_TYPES[0].key);
  const [annualCost, setAnnualCost] = useState(30990);
  const [costRise, setCostRise] = useState(5);
  const [duration, setDuration] = useState(4);
  const [pctFromSavings, setPctFromSavings] = useState(35);
  const [balance, setBalance] = useState(0);
  const [returnRate, setReturnRate] = useState(5);
  const [taxRate, setTaxRate] = useState(25);
  const [startsIn, setStartsIn] = useState(3);

  const onTypeChange = (k) => {
    setCollegeType(k);
    const t = COLLEGE_TYPES.find(t => t.key === k);
    if (t) setAnnualCost(t.cost);
  };

  const c = useMemo(() => {
    const rise = costRise / 100;
    // After-tax net return during savings phase
    const netRet = (returnRate / 100) * (1 - taxRate / 100);

    // Total future cost across duration years (starting in `startsIn` years)
    let totalCost = 0;
    for (let i = 0; i < duration; i++) {
      const yearIdx = startsIn + i; // years from today
      totalCost += annualCost * Math.pow(1 + rise, yearIdx);
    }
    // Freshman year cost
    const freshFuture = annualCost * Math.pow(1 + rise, startsIn);
    // Present value of totalCost using net return
    const totalToday = netRet === 0 ? totalCost : totalCost / Math.pow(1 + netRet, startsIn);

    // Equivalent monthly saving that would fund the FULL cost given balance + returns
    // Standard PMT for future value of annuity + growth of current balance
    const nMonths = Math.max(1, startsIn * 12);
    const rMon = Math.pow(1 + netRet, 1/12) - 1;
    const fvBalance = balance * Math.pow(1 + rMon, nMonths);
    const targetFull = totalCost; // dollars needed at start of college
    const pmtFull = rMon === 0 ? (targetFull - fvBalance) / nMonths : (targetFull - fvBalance) * rMon / (Math.pow(1 + rMon, nMonths) - 1);

    // If 35% comes from savings scenario
    const savingsTarget = totalCost * pctFromSavings / 100;
    const savingsToday = netRet === 0 ? savingsTarget : savingsTarget / Math.pow(1 + netRet, startsIn);
    const pmtPart = rMon === 0 ? (savingsTarget - fvBalance) / nMonths : (savingsTarget - fvBalance) * rMon / (Math.pow(1 + rMon, nMonths) - 1);
    const freshFromSavings = freshFuture * pctFromSavings / 100;

    return {
      totalCost, totalToday, pmtFull, freshFuture, freshNow: annualCost,
      savingsTarget, savingsToday, pmtPart, freshFromSavings
    };
  }, [annualCost, costRise, duration, pctFromSavings, balance, returnRate, taxRate, startsIn]);

  return (
    <CalcShell look="cnet" title="College Cost Calculator" subtitle="How Much Does College Cost?"
      headerColor="blue"
      about={<>
        <p>The <b>College Cost Calculator</b> estimates the future cost of college accounting for tuition inflation and the equivalent monthly saving needed to fund it — either in full or a portion of it — with the rest covered by grants, scholarships, or student loans.</p>
      </>}
      slug="college-cost"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Inputs */}
        <div>
          <div className="field">
            <label>Today's annual college costs</label>
            <div className="flex gap-2 items-center">
              <span className="text-slate-500">$</span>
              <input type="number" value={annualCost} onChange={e=>setAnnualCost(+e.target.value||0)} className="flex-1" data-testid="cc-cost"/>
            </div>
            <select value={collegeType} onChange={e=>onTypeChange(e.target.value)} className="mt-2 text-sm">
              {COLLEGE_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}: ${t.cost.toLocaleString()}</option>)}
            </select>
          </div>
          <div className="field"><label>College cost increase rate</label>
            <div className="flex gap-2"><input type="number" step="0.1" value={costRise} onChange={e=>setCostRise(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span>
              <span className="self-center text-xs text-slate-400 whitespace-nowrap">5% recommended</span></div>
          </div>
          <div className="field"><label>Expected college attendance duration</label>
            <div className="flex gap-2"><input type="number" value={duration} onChange={e=>setDuration(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">years</span></div>
          </div>
          <div className="field"><label>Percent of costs from savings</label>
            <div className="flex gap-2"><input type="number" value={pctFromSavings} onChange={e=>setPctFromSavings(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div>
          </div>
          <div className="field"><label>College savings balance now</label>
            <div className="flex gap-2 items-center"><span className="text-slate-500">$</span>
              <input type="number" value={balance} onChange={e=>setBalance(+e.target.value||0)} className="flex-1"/>
              <span className="text-xs text-slate-400 whitespace-nowrap">amount saved so far</span></div>
          </div>
          <div className="field"><label>Interest or investment return rate</label>
            <div className="flex gap-2"><input type="number" step="0.1" value={returnRate} onChange={e=>setReturnRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div>
          </div>
          <div className="field"><label>Tax rate on interest or investment return</label>
            <div className="flex gap-2"><input type="number" step="0.1" value={taxRate} onChange={e=>setTaxRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span>
              <span className="self-center text-xs text-slate-400 whitespace-nowrap">use 0% for 529 plan</span></div>
          </div>
          <div className="field"><label>College will start in</label>
            <div className="flex gap-2"><input type="number" value={startsIn} onChange={e=>setStartsIn(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">years</span></div>
          </div>
        </div>

        {/* Results */}
        <div>
          <div className="result-panel">
            <div className="result-title">Results:</div>

            <h4 className="font-bold text-blue-800 mt-1 mb-2">If paying college costs in full</h4>
            <table className="w-full text-sm mb-4">
              <tbody>
                <tr><td className="py-1">Total college cost</td><td className="text-right font-bold" data-testid="cc-total">{fmtInt(c.totalCost)}</td></tr>
                <tr><td className="py-1">Total college cost in today's money</td><td className="text-right bg-slate-100 px-2">{fmtInt(c.totalToday)}</td></tr>
                <tr><td className="py-1">Equivalent monthly saving</td><td className="text-right"><b>{fmtInt(c.pmtFull)}</b> per month for {startsIn} years</td></tr>
                <tr><td className="py-1">Freshman year cost (rise {costRise}% for {startsIn} years)</td><td className="text-right">{fmtInt(c.freshFuture)} <span className="text-slate-500">v.s. {fmtInt(c.freshNow)} now</span></td></tr>
              </tbody>
            </table>

            <h4 className="font-bold text-blue-800 mt-4 mb-2">If {pctFromSavings}% of college costs come from savings</h4>
            <p className="text-xs text-slate-600 mb-2">The rest maybe paid by grants, scholarships, student loans, or other financial aids.</p>
            <table className="w-full text-sm">
              <tbody>
                <tr><td className="py-1">You will need to save</td><td className="text-right font-bold" data-testid="cc-savings-target">{fmtInt(c.savingsTarget)}</td></tr>
                <tr><td className="py-1">Your target saving amount in today's money</td><td className="text-right bg-slate-100 px-2">{fmtInt(c.savingsToday)}</td></tr>
                <tr><td className="py-1">Equivalent monthly saving</td><td className="text-right"><b>{fmtInt(c.pmtPart)}</b> per month for {startsIn} years</td></tr>
                <tr><td className="py-1">Amount needed in freshman year:</td><td className="text-right">{fmtInt(c.freshFromSavings)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Reference: Average annual U.S. college costs */}
      <div className="mt-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">Reference — Average Annual U.S. College Costs</h3>
        <div className="overflow-auto">
          <table className="w-full text-sm border border-slate-200">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-3 py-2 text-left border border-slate-200">Type of College</th>
                <th className="px-3 py-2 text-right border border-slate-200">Tuition &amp; Fees + Room &amp; Board</th>
              </tr>
            </thead>
            <tbody>
              {COLLEGE_TYPES.map(t => (
                <tr key={t.key} className={t.key===collegeType?'bg-blue-50':''}>
                  <td className="px-3 py-2 border border-slate-200">{t.label}</td>
                  <td className="px-3 py-2 border border-slate-200 text-right">${t.cost.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <StepsPanel steps={[
        `Future annual cost year i = today × (1 + rise)^(startsIn + i)`,
        `Total college cost = Σ future annual cost across ${duration} years = <b>${fmtInt(c.totalCost)}</b>`,
        `After‑tax net return = ${returnRate}% × (1 − ${taxRate}%) = ${((returnRate/100)*(1-taxRate/100)*100).toFixed(3)}%`,
        `Monthly PMT solves: FV_balance + PMT × [((1+r)^n − 1)/r] = target`
      ]} />
    </CalcShell>
  );
}
